import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Alert, Avatar, Button, Input, InputNumber, Radio, Segmented, Space, Table, Tag, Tooltip,
  Typography,
} from 'antd';
import {
  AppstoreOutlined, PlayCircleOutlined, ReloadOutlined, UnorderedListOutlined, WarningOutlined,
} from '@ant-design/icons';
import * as fmt from '../format';
import { useSaveBeerRule } from '../hooks';
import { beerRulePayload } from '../RulesTab/BeerRules';
import CoreRangeChart from './CoreRangeChart';
import { FAMILIES, classifySku, isStocked } from './skuFormat';

const { Text } = Typography;

const FILTERS = [
  { id: 'all', label: 'All beers' },
  { id: 'action', label: 'Needs a brew in 2 weeks' },
  { id: 'overstock', label: 'Over max cover' },
];

const VIEWS = [
  { value: 'cover', label: 'Cover', icon: <AppstoreOutlined /> },
  { value: 'detail', label: 'Detail', icon: <UnorderedListOutlined /> },
];

const SORTS = [
  { value: 'urgency', label: 'Most urgent' },
  { value: 'name', label: 'A–Z' },
];

const UNITS = [
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
];

const RANGES = [
  { value: 'core', label: 'Core' },
  { value: 'others', label: 'Others' },
  { value: 'combined', label: 'Combined' },
];

const STATUS_RANK = {
  urgent: 0, brew_soon: 1, ok: 2, overstock: 3, excluded: 4,
};

/** Lowest SKU cover relative to min, so 3 wk against a 6 wk min outranks 4 wk against 4. */
const pressure = (beer) => {
  const min = (beer.rules && beer.rules.minCoverWeeks) || 1;
  return (beer.skuLines || []).reduce((acc, line) => (
    line && !line.viaMix && line.weeksOnHand != null
      ? Math.min(acc, line.weeksOnHand / min)
      : acc
  ), Infinity);
};

const byUrgency = (a, b) => (
  (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9)
  || pressure(a) - pressure(b)
  || a.beerName.localeCompare(b.beerName)
);

const byName = (a, b) => a.beerName.localeCompare(b.beerName);

const inFamily = (family) => (line) => (
  family === 'all' || (line && classifySku(line).family === family)
);

export const STOCK_BUCKETS = [
  ['nz', 'NZ'],
  ['wip', 'WIP'],
  ['aus', 'AUS'],
  ['export', 'Export'],
  ['auTransit', 'AU Transit'],
];

const GlanceCard = ({ label, lines }) => (
  <div className="forecasting-stat">
    <Text type="secondary">{label}</Text>
    <div className="forecasting-stat-lines">
      {lines.map((line) => (
        <div key={line.id} className="forecasting-stat-line">
          <Text type="secondary">{line.label}</Text>
          <Text strong>{line.value}</Text>
        </div>
      ))}
    </div>
  </div>
);

GlanceCard.propTypes = {
  label: PropTypes.string.isRequired,
  lines: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.node.isRequired,
  })).isRequired,
};

const datedLabel = (name, date) => (date ? `${name} · ${fmt.shortDate(date)}` : name);

const WeeksOnHand = ({ weeks, rules, asMonths }) => {
  const text = asMonths ? fmt.months(weeks) : fmt.weeks(weeks);
  const limit = (value) => (asMonths ? fmt.months(value) : `${value} wk`);
  if (weeks == null || !rules) return text;
  if (weeks < rules.minCoverWeeks) {
    return (
      <Tooltip title={`Below ${limit(rules.minCoverWeeks)} minimum`}>
        <span className="forecasting-weeks-low">{text}</span>
      </Tooltip>
    );
  }
  if (weeks > rules.maxCoverWeeks) {
    return (
      <Tooltip title={`Above ${limit(rules.maxCoverWeeks)} maximum`}>
        <span className="forecasting-weeks-high">{text}</span>
      </Tooltip>
    );
  }
  return text;
};

WeeksOnHand.propTypes = {
  weeks: PropTypes.number,
  rules: PropTypes.shape({
    minCoverWeeks: PropTypes.number,
    maxCoverWeeks: PropTypes.number,
  }),
  asMonths: PropTypes.bool,
};

WeeksOnHand.defaultProps = { weeks: null, rules: null, asMonths: false };

export const AuTransitInput = ({ beer }) => {
  const saveRule = useSaveBeerRule();
  const cin7 = beer.auTransitCin7Units || 0;
  const override = beer.rules.auTransitUnits;
  const started = override == null ? cin7 : Number(override);

  const commit = (raw) => {
    if (saveRule.isLoading) return;
    const next = raw === '' || raw == null ? null : Number(raw);
    if (next != null && !Number.isFinite(next)) return;
    if (next == null && override == null) return;
    if (next != null && next === started) return;
    const auTransitUnits = next != null && next === cin7 ? null : next;
    if (auTransitUnits == null && override == null) return;
    saveRule.mutate(beerRulePayload(beer, { auTransitUnits }));
  };

  return (
    <InputNumber
      key={`${beer.beerName}-${override}`}
      size="small"
      min={0}
      className="forecasting-au-transit"
      defaultValue={started}
      onClick={(e) => e.stopPropagation()}
      onPressEnter={(e) => e.currentTarget.blur()}
      onBlur={(e) => commit(e.target.value)}
    />
  );
};

AuTransitInput.propTypes = {
  beer: PropTypes.shape({
    beerName: PropTypes.string,
    auTransitCin7Units: PropTypes.number,
    rules: PropTypes.shape({ auTransitUnits: PropTypes.number }),
  }).isRequired,
};

const skuRowKey = (line) => `${line.sku}::${line.viaMix || ''}`;

const weeklyUnits = (line) => {
  if (!line || line.forecastWeeklyLitres == null) return null;
  const per = Number(line.litresPerUnit);
  if (!(per > 0)) return null;
  const units = Number(line.forecastWeeklyLitres) / per;
  return Number.isFinite(units) ? fmt.number(Math.round(units)) : null;
};

const beerSpan = (row) => ({ rowSpan: row.rowSpan });
const spanMiddle = (row) => ({ rowSpan: row.rowSpan, className: 'forecasting-span-middle' });
const mixSkuCell = (row) => (
  row.line && row.line.viaMix ? { className: 'forecasting-mix-sku' } : {}
);

const BeerCell = ({ beer, canEdit }) => {
  const buckets = beer.stockUnitsByBucket || {};
  const warnings = beer.warnings || [];
  return (
    <div className="forecasting-beer-cell">
      {beer.tapBadgeUrl ? (
        <Avatar className="forecasting-tap-badge" src={beer.tapBadgeUrl} size={56} alt="" />
      ) : (
        <span className="forecasting-tap-badge-empty" />
      )}
      <Space direction="vertical" size={2}>
        <Text strong>{beer.beerName}</Text>
        <Space size={4} wrap>
          {beer.method && beer.method !== 'arima_ensemble' && (
            <Tag color={fmt.METHOD[beer.method]?.color}>{fmt.METHOD[beer.method]?.label}</Tag>
          )}
          {warnings.length > 0 && (
            <Tooltip title={warnings.join(' · ')}>
              <Text type="warning"><WarningOutlined /></Text>
            </Tooltip>
          )}
        </Space>
        <div
          className="forecasting-beer-transit"
          onClick={(e) => e.stopPropagation()}
          role="presentation"
        >
          <Text type="secondary">AU transit</Text>
          {canEdit
            ? <AuTransitInput beer={beer} />
            : <span>{fmt.number(buckets.auTransit || 0)}</span>}
        </div>
      </Space>
    </div>
  );
};

BeerCell.propTypes = {
  beer: PropTypes.shape({
    beerName: PropTypes.string,
    tapBadgeUrl: PropTypes.string,
    method: PropTypes.string,
    warnings: PropTypes.arrayOf(PropTypes.string),
    stockUnitsByBucket: PropTypes.shape({ auTransit: PropTypes.number }),
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
};

const PlanTab = ({
  plan, canEdit, running, runDisabled, refreshLoading, onRunForecast, onRefresh, onOpenBeer,
}) => {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [forecastAsUnits, setForecastAsUnits] = useState(true);
  const [coverAsMonths, setCoverAsMonths] = useState(false);
  const [view, setView] = useState('cover');
  const [family, setFamily] = useState('all');
  const [sort, setSort] = useState('urgency');
  const [range, setRange] = useState('combined');
  const soonCutoff = dayjs(plan.today).add(14, 'day').format('YYYY-MM-DD');
  const glance = plan.stockGlance && plan.stockGlance.ranges
    ? plan.stockGlance.ranges[range]
    : null;

  const families = useMemo(() => {
    const present = new Set(plan.beers.flatMap((b) => (b.skuLines || [])
      .filter((line) => line && !line.viaMix && isStocked(line))
      .map((line) => classifySku(line).family)));
    return FAMILIES
      .filter((item) => item.id === 'all' || present.has(item.id))
      .map((item) => ({ value: item.id, label: item.label }));
  }, [plan]);

  const rows = useMemo(() => plan.beers
    .filter((b) => {
      if (search && !b.beerName.toLowerCase().includes(search.toLowerCase())) return false;
      if (family !== 'all' && !(b.skuLines || []).some(inFamily(family))) return false;
      if (filter === 'action') {
        return b.nextRecommendation && b.nextRecommendation.brewDate <= soonCutoff;
      }
      if (filter === 'overstock') return b.status === 'overstock';
      return true;
    })
    .sort(sort === 'name' ? byName : byUrgency), [plan, filter, search, family, sort, soonCutoff]);

  const order = rows.map((r) => r.beerName);
  const flatRows = useMemo(() => rows.flatMap((beer) => {
    const own = (beer.skuLines || []).filter(inFamily(family));
    const lines = own.length ? own : [null];
    return lines.map((line, i) => ({
      key: line ? `${beer.beerName}::${skuRowKey(line)}` : beer.beerName,
      beer,
      line,
      rowSpan: i === 0 ? lines.length : 0,
    }));
  }), [rows, family]);
  const columns = [
    {
      title: 'Beer',
      onCell: spanMiddle,
      render: (_, row) => (row.rowSpan ? <BeerCell beer={row.beer} canEdit={canEdit} /> : null),
    },
    {
      title: 'Status',
      onCell: spanMiddle,
      render: (_, row) => (row.rowSpan ? (
        <Tag color={fmt.STATUS[row.beer.status].color}>{fmt.STATUS[row.beer.status].label}</Tag>
      ) : null),
    },
    {
      title: 'SKU',
      onCell: mixSkuCell,
      render: (_, row) => {
        if (!row.line) return <Text type="secondary">No SKUs</Text>;
        if (!row.line.viaMix) return row.line.sku;
        return <Tooltip title={`in ${row.line.viaMix}`}>{row.line.sku}</Tooltip>;
      },
    },
    ...STOCK_BUCKETS.map(([key, label]) => ({
      title: key === 'auTransit' ? (
        <Tooltip title="Cin7 units per SKU. The beer cell is the planning total.">
          {label}
        </Tooltip>
      ) : label,
      key,
      align: 'right',
      width: 88,
      onCell: mixSkuCell,
      render: (_, row) => (
        row.line
          ? fmt.number((row.line.stockUnitsByBucket || {})[key] || 0)
          : '—'
      ),
    })),
    {
      title: (
        <Tooltip title={forecastAsUnits ? 'Show litres' : 'Show units'}>
          <span className="forecasting-forecast-toggle">Forecast / wk</span>
        </Tooltip>
      ),
      key: 'forecast',
      align: 'right',
      width: 110,
      onHeaderCell: () => ({
        onClick: () => setForecastAsUnits((on) => !on),
        className: 'forecasting-forecast-header',
      }),
      onCell: mixSkuCell,
      render: (_, row) => (
        forecastAsUnits
          ? (weeklyUnits(row.line) ?? '—')
          : fmt.litres(row.line ? row.line.forecastWeeklyLitres : null)
      ),
    },
    {
      title: (
        <Tooltip title={coverAsMonths ? 'Show weeks' : 'Show months'}>
          <span className="forecasting-forecast-toggle">
            {coverAsMonths ? 'Months on hand' : 'Weeks on hand'}
          </span>
        </Tooltip>
      ),
      key: 'weeks',
      align: 'right',
      width: 148,
      onHeaderCell: () => ({
        onClick: () => setCoverAsMonths((on) => !on),
        className: 'forecasting-forecast-header',
      }),
      onCell: mixSkuCell,
      render: (_, row) => (
        <WeeksOnHand
          weeks={row.line ? row.line.weeksOnHand : null}
          rules={row.beer.rules}
          asMonths={coverAsMonths}
        />
      ),
    },
    {
      title: 'In tank / planned',
      align: 'right',
      onCell: beerSpan,
      render: (_, row) => (row.rowSpan ? (
        <Space direction="vertical" size={0} align="end">
          <span>
            {`${fmt.litres(row.beer.tankLitres)} / ${fmt.litres(row.beer.plannedLitres)}`}
          </span>
          <Text type="secondary">{`${fmt.weeks(row.beer.coverWithPipelineWeeks)} incl.`}</Text>
        </Space>
      ) : null),
    },
    {
      title: 'Next planned brew',
      onCell: beerSpan,
      render: (_, row) => {
        if (!row.rowSpan) return null;
        const brew = row.beer.nextPlannedBrew;
        if (!brew) return <Text type="secondary">None</Text>;
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{fmt.date(brew.brewDate)}</Text>
            <Text>{`${fmt.litres(brew.volumeLitres)} · ${brew.brewery}`}</Text>
          </Space>
        );
      },
    },
  ];
  const undecomposed = plan.mixPacks.filter((m) => !m.decomposed);

  return (
    <div className="forecasting-plan">
      <div className="forecasting-glance">
        <div className="forecasting-glance-head">
          <Text strong>Stock on hand</Text>
          <Segmented options={RANGES} value={range} onChange={setRange} />
        </div>
        <div className="forecasting-stat-grid">
          <GlanceCard
            label="Value · NZD"
            lines={[
              {
                id: 'combined',
                label: 'Combined',
                value: fmt.money(glance ? glance.value.combined : null),
              },
              {
                id: 'nz',
                label: datedLabel('NZ', plan.stockGlance && plan.stockGlance.nzSnapshotDate),
                value: fmt.money(glance ? glance.value.nz : null),
              },
              {
                id: 'au',
                label: datedLabel('AU', plan.stockGlance && plan.stockGlance.auSnapshotDate),
                value: fmt.money(glance ? glance.value.au : null),
              },
            ]}
          />
          <GlanceCard
            label="Packaged units"
            lines={[
              { id: 'can', label: 'Cans', value: fmt.number(glance ? glance.units.can : null) },
              { id: 'keg', label: 'Kegs', value: fmt.number(glance ? glance.units.keg : null) },
              {
                id: 'bottle',
                label: 'Bottles',
                value: fmt.number(glance ? glance.units.bottle : null),
              },
            ]}
          />
          <div className="forecasting-stat forecasting-data-card">
            <Text type="secondary">Forecast data</Text>
            <div className="forecasting-stat-lines">
              <div className="forecasting-stat-line forecasting-data-line">
                <div className="forecasting-data-copy">
                  <Text type="secondary">Forecast run</Text>
                  <Text strong>
                    {plan.run ? dayjs(plan.run.runTs).format('ddd D MMM, h:mma') : '—'}
                  </Text>
                  {plan.run && (
                    <Text type="secondary">
                      {`Sales through ${fmt.date(plan.run.demandThrough)}`}
                    </Text>
                  )}
                  {running && <Text type="secondary">Forecast is rebuilding</Text>}
                </div>
                {canEdit && (
                  <Tooltip title="Rebuild the forecast in BigQuery (about 5 minutes)">
                    <Button
                      icon={<PlayCircleOutlined />}
                      loading={running}
                      disabled={runDisabled}
                      onClick={onRunForecast}
                    >
                      Run forecast
                    </Button>
                  </Tooltip>
                )}
              </div>
              <div className="forecasting-stat-line forecasting-data-line">
                <div className="forecasting-data-copy">
                  <Text type="secondary">Stock snapshot</Text>
                  <Text strong>{fmt.date(plan.snapshotDate)}</Text>
                </div>
                <Tooltip
                  title="Re-read forecast and stock from BigQuery (otherwise cached for 15 minutes)"
                >
                  <Button
                    icon={<ReloadOutlined />}
                    loading={refreshLoading}
                    disabled={running}
                    onClick={onRefresh}
                  >
                    Refresh data
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>

      {undecomposed.length > 0 && (
        <Alert
          className="forecasting-section"
          type="info"
          showIcon
          message={`Mixed packs without a composition: ${undecomposed.map((m) => m.beerName).join(', ')}`}
          description="They're planned as their own line. Set their component beers under Rules & targets."
        />
      )}

      <div className="forecasting-core forecasting-section">
        <div className="forecasting-core-head">
          <div>
            <Text strong>Core range</Text>
            <div>
              <Text type="secondary">
                {view === 'cover'
                  ? "Stock on hand per pack format against each beer's target range"
                  : 'Stock by location, forecast and pipeline per SKU'}
              </Text>
            </div>
          </div>
          <Segmented options={VIEWS} value={view} onChange={setView} />
        </div>
        <div className="forecasting-core-toolbar">
          <Radio.Group value={filter} onChange={(e) => setFilter(e.target.value)} buttonStyle="solid">
            {FILTERS.map((f) => <Radio.Button key={f.id} value={f.id}>{f.label}</Radio.Button>)}
          </Radio.Group>
          <Input.Search
            allowClear
            placeholder="Search beers"
            style={{ width: 200 }}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="forecasting-core-spacer" />
          <Segmented options={families} value={family} onChange={setFamily} />
          <Segmented options={SORTS} value={sort} onChange={setSort} />
          <Segmented
            options={UNITS}
            value={coverAsMonths ? 'months' : 'weeks'}
            onChange={(value) => setCoverAsMonths(value === 'months')}
          />
        </div>

        {view === 'cover' ? (
          <CoreRangeChart
            beers={rows}
            family={family}
            asMonths={coverAsMonths}
            onOpenBeer={onOpenBeer}
          />
        ) : (
          <Table
            rowKey="key"
            className="forecasting-plan-table"
            size="small"
            columns={columns}
            dataSource={flatRows}
            pagination={false}
            rowClassName={(row) => (
              row.rowSpan ? 'forecasting-beer-row forecasting-beer-group' : 'forecasting-beer-row'
            )}
            onRow={(row) => ({
              onClick: () => onOpenBeer(row.beer.beerName, order),
            })}
            locale={{
              emptyText: filter === 'action' ? 'No brews needed in the next 2 weeks' : 'No beers',
            }}
          />
        )}
      </div>
    </div>
  );
};

PlanTab.propTypes = {
  plan: PropTypes.shape({
    today: PropTypes.string,
    beers: PropTypes.arrayOf(PropTypes.object),
    mixPacks: PropTypes.arrayOf(PropTypes.object),
    run: PropTypes.shape({
      runTs: PropTypes.string,
      demandThrough: PropTypes.string,
    }),
    snapshotDate: PropTypes.string,
    stockGlance: PropTypes.shape({
      nzSnapshotDate: PropTypes.string,
      auSnapshotDate: PropTypes.string,
      ranges: PropTypes.objectOf(PropTypes.shape({
        value: PropTypes.shape({
          nz: PropTypes.number,
          au: PropTypes.number,
          combined: PropTypes.number,
        }),
        units: PropTypes.shape({
          can: PropTypes.number,
          keg: PropTypes.number,
          bottle: PropTypes.number,
        }),
      })),
    }),
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
  running: PropTypes.bool.isRequired,
  runDisabled: PropTypes.bool.isRequired,
  refreshLoading: PropTypes.bool.isRequired,
  onRunForecast: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onOpenBeer: PropTypes.func.isRequired,
};

export default PlanTab;
